import {json, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData} from '@remix-run/react';
import type {Collection as CollectionType} from '@shopify/hydrogen/storefront-api-types';
import {AnalyticsPageType} from '@shopify/hydrogen';
import invariant from 'tiny-invariant';
import {routeHeaders} from '~/data/cache';

//domains
import {
  getAllParentCollections,
  getCollections,
  getRelatedCollections,
  extractGids,
  useCollectionQuery as fetchCollectionData,
  PRODUCT_PER_PAGE,
  PRODUCT_PER_PAGE_WITHOUT_ADS,
} from 'domains/collections';
import {
  parseNativeFilterParams,
  legacyBoostParamsToNative,
  sortToNative,
  resolveOffsetCursor,
  countProductsByScan,
  deriveTotalProducts,
  extractPriceBounds,
  nativeProductToBfs,
  type NativeFilter,
  type NativeProductNode,
  FILTER_PARAM_PREFIX,
  NATIVE_COLLECTION_PAGE_QUERY,
  NATIVE_FACETS_QUERY,
  fetchTypeGroupedWindow,
  VENDOR_FILTER_ID,
  PRODUCT_TYPE_FACET_ID,
} from 'domains/nativeCollection';

import {consolidatedCollectionTarget} from '~/lib/collectionRedirects';
import {legacyRedirectTarget} from '~/lib/legacyRedirects';
import {seoPayload, canonicalUrl} from '~/lib/seo.server';

//translation
import translation from 'translations/collection.json';
import tagTranslation from 'translations/tags.json';
import {routeLocale} from 'domains/global';
import {brandForCollection} from '~/lib/brandCollections';
import {modelsForVendor, selectedBrandModel} from '~/lib/brandModels';

//components
import Container from 'components/Container';
import CollectionMain from 'components/collection/CollectionMain';

export const headers = routeHeaders;

/**
 * PLP responses are anonymous (currency converts client-side; the TW site is
 * its own host, language its own path) so let the edge serve repeat filter
 * combinations instantly and refresh in the background. Totals can lag live
 * inventory by up to ~a minute — same class of staleness Boost's own result
 * cache had.
 */
const PLP_CACHE_CONTROL =
  'public, max-age=1, s-maxage=45, stale-while-revalidate=600';

export type SortParam =
  | 'price-low-high'
  | 'price-high-low'
  | 'best-selling'
  | 'newest'
  | 'featured';

interface NativePageData {
  collection: {
    id: string;
    products: {
      filters: NativeFilter[];
      pageInfo: {hasNextPage: boolean; endCursor: string | null};
      nodes: NativeProductNode[];
    };
  } | null;
}

interface CollectionChrome {
  hasAds: boolean;
  collectionAdvertisement: Awaited<
    ReturnType<typeof fetchCollectionData>
  >['collectionAdvertisement'];
  defaultAdvertisement: Awaited<
    ReturnType<typeof fetchCollectionData>
  >['defaultAdvertisement']['productSettings']['product_settings_main'];
  collectionBreadcrumb: Awaited<ReturnType<typeof getCollections>>[0];
  relatedCollections: ReturnType<typeof getRelatedCollections>;
  collectionsTranslated: Awaited<ReturnType<typeof getCollections>>[0];
}

/**
 * The Sanity structure/ads and breadcrumb/related lookups depend only on the
 * collection handle + language, so cache them briefly: filter/sort/page
 * clicks on the same PLP skip four upstream round-trips and feel instant.
 * Best-effort per serverless instance; the promise is cached to dedupe
 * concurrent requests and evicted on failure.
 */
const CHROME_TTL_MS = 120_000;
const chromeCache = new Map<
  string,
  {expires: number; promise: Promise<CollectionChrome>}
>();

function getCollectionChrome(
  key: string,
  factory: () => Promise<CollectionChrome>,
): Promise<CollectionChrome> {
  const hit = chromeCache.get(key);
  if (hit && hit.expires > Date.now()) return hit.promise;
  const promise = factory();
  promise.catch(() => chromeCache.delete(key));
  chromeCache.set(key, {expires: Date.now() + CHROME_TTL_MS, promise});
  return promise;
}

async function loadCollectionChrome({
  collectionPromise,
  collectionHandle,
  context,
}: {
  collectionPromise: Promise<{collection: CollectionType}>;
  collectionHandle: string;
  context: LoaderArgs['context'];
}): Promise<CollectionChrome> {
  const [{collection}, collectionSanityData] = await Promise.all([
    collectionPromise,
    fetchCollectionData(collectionHandle),
  ]);

  if (!collection) {
    // Renamed/retired handles live in the ported Shopify redirect table.
    // pathPrefix keeps the /en variant on its own locale.
    const prefix = context.storefront.i18n.pathPrefix.replace(/\/$/, '');
    const legacy = legacyRedirectTarget(
      `${prefix}/collections/${collectionHandle}`,
    );
    if (legacy) {
      throw redirect(legacy, 301);
    }
    throw new Response(null, {status: 404});
  }

  const collectionAdvertisement = collectionSanityData.collectionAdvertisement;
  const defaultAdvertisement =
    collectionSanityData.defaultAdvertisement.productSettings
      .product_settings_main;
  const hasAds = Boolean(
    collectionAdvertisement?.advertisement?.ad_is_active ||
      defaultAdvertisement?.advertisement?.ad_is_active,
  );

  const allParentCollections = getAllParentCollections({
    collectionId: collection.id,
    collectionStructure: collectionSanityData.collectionStructure,
  });

  const allParentCollectionIds = allParentCollections
    ? [...allParentCollections.map((c) => c?.store?.gid)]
    : [];

  const collectionIds = [...allParentCollectionIds, collection.id].flat();

  const [collectionBreadcrumb] = await getCollections({
    collectionIds,
    context,
  });

  const relatedCollections = getRelatedCollections({
    collectionId: collection.id,
    collectionStructure: collectionSanityData.collectionStructure,
  });

  const allGids = relatedCollections && extractGids(relatedCollections as any);

  const [collectionsTranslated] = await getCollections({
    collectionIds: allGids ?? [],
    context,
  });

  return {
    hasAds,
    collectionAdvertisement,
    defaultAdvertisement,
    collectionBreadcrumb,
    relatedCollections,
    collectionsTranslated,
  };
}

export async function loader({params, request, context}: LoaderArgs) {
  const {collectionHandle} = params;

  invariant(collectionHandle, 'Missing collectionHandle param');

  const url = new URL(request.url);
  const searchParams = url.searchParams;

  // Old Boost pf_* URLs (indexed links, bookmarks) → native params
  const legacyParams = legacyBoostParamsToNative(searchParams);
  if (legacyParams) {
    const qs = legacyParams.toString();
    return redirect(`${url.pathname}${qs ? `?${qs}` : ''}`, 301);
  }

  // Collections that were folded into a single canonical PLP send visitors to
  // the merged page with their product type pre-selected. Done before any
  // Shopify round-trip so a retired handle costs nothing.
  const consolidatedTarget = consolidatedCollectionTarget(
    collectionHandle,
    url.pathname,
    searchParams,
  );
  if (consolidatedTarget) {
    return redirect(consolidatedTarget, 301);
  }

  const {country, language} = context.storefront.i18n;

  // Fired now, awaited alongside the page query below — it only needs the
  // handle, so the two Shopify round-trips run in parallel instead of
  // back-to-back.
  const collectionPromise = context.storefront.query<{
    collection: CollectionType;
  }>(COLLECTION_QUERY, {
    variables: {
      handle: collectionHandle,
      country,
      language,
    },
  });

  const {
    hasAds,
    collectionAdvertisement,
    defaultAdvertisement,
    collectionBreadcrumb,
    relatedCollections,
    collectionsTranslated,
  } = await getCollectionChrome(`${collectionHandle}|${language}`, () =>
    loadCollectionChrome({collectionPromise, collectionHandle, context}),
  );

  const productPerPage = hasAds
    ? PRODUCT_PER_PAGE
    : PRODUCT_PER_PAGE_WITHOUT_ADS;

  const parsed = parseNativeFilterParams(searchParams, {
    // Preorder products are, by definition, always out of stock — so the
    // site-wide "hide out-of-stock" default would empty this page. Show them.
    skipInStockDefault: collectionHandle === 'preorder-products',
  });
  const {sortKey, reverse} = sortToNative(parsed.sort);
  const offset = (parsed.page - 1) * productPerPage;

  const queryArgs = {
    storefront: context.storefront,
    handle: collectionHandle,
    filters: parsed.filters,
    sortKey,
    reverse,
    country,
    language,
  };

  // Default ordering groups by product type, newest-listed first within
  // each type (multi-type pages only — single-type pages read the same
  // either way and fall through). An explicit ?sort= choice overrides the
  // grouping across the whole set.
  // parseNativeFilterParams defaults sort to the string 'manual', so test
  // the raw param — only an explicit ?sort= choice opts out of grouping.
  const grouped = !searchParams.has('sort')
    ? await fetchTypeGroupedWindow({
        storefront: context.storefront,
        handle: collectionHandle,
        filters: parsed.filters,
        offset,
        first: productPerPage,
        country,
        language,
      })
    : null;

  const {after, outOfRange} = grouped
    ? {after: null, outOfRange: grouped.outOfRange}
    : await resolveOffsetCursor({
        ...queryArgs,
        offset,
      });

  // A page number past the end still answered 200 with an empty grid and a
  // self-canonical, so ?page=999 — and every number after it — was an
  // indexable URL. Google files those as soft 404s and spends the crawl
  // budget getting there. Bailing before the product queries also keeps the
  // Storefront API out of it. Page 1 is left alone: an empty collection is
  // still a real page.
  if (outOfRange && parsed.page > 1) {
    throw new Response(null, {status: 404});
  }

  const [pageData, priceBoundsData, {collection}] = await Promise.all([
    grouped
      ? Promise.resolve(null)
      : context.storefront.query<NativePageData>(NATIVE_COLLECTION_PAGE_QUERY, {
          variables: {
            handle: collectionHandle,
            filters: parsed.filters,
            sortKey,
            reverse,
            first: productPerPage,
            after,
            country,
            language,
          },
        }),
    parsed.hasPriceFilter
      ? context.storefront.query<NativePageData>(NATIVE_FACETS_QUERY, {
          variables: {
            handle: collectionHandle,
            filters: parsed.filters.filter((f) => !('price' in f)),
            country,
            language,
          },
        })
      : Promise.resolve(null),
    collectionPromise,
  ]);

  if (!collection) {
    const legacy = legacyRedirectTarget(new URL(request.url).pathname);
    if (legacy) {
      throw redirect(legacy, 301);
    }
    throw new Response(null, {status: 404});
  }

  const productsConnection = pageData?.collection?.products;
  const facets = grouped ? grouped.facets : productsConnection?.filters ?? [];

  let totalProducts = grouped
    ? grouped.totalProducts
    : deriveTotalProducts(facets, parsed.filters);
  if (totalProducts === null) {
    totalProducts = await countProductsByScan(queryArgs);
  }

  const priceBounds = extractPriceBounds(
    priceBoundsData?.collection?.products?.filters ?? facets,
  );

  const products = (
    grouped ? grouped.nodes : outOfRange ? [] : productsConnection?.nodes ?? []
  ).map(nativeProductToBfs);

  // outOfRange misses the exact boundary: page 2 of a collection holding
  // exactly one page of products resolves a cursor at the very last item and
  // then returns nothing. Same empty page, same soft 404.
  if (parsed.page > 1 && products.length === 0) {
    throw new Response(null, {status: 404});
  }

  // Paginated pages must self-canonicalise: ?page=2 lists products that
  // appear on no other URL, so pointing its canonical at page 1 asks Google
  // to drop them. Faceted and sorted permutations still collapse onto the
  // bare PLP — those are re-cuts of the same set, not new pages.
  //
  // Test the raw params, not parsed.filters: the parser always appends the
  // site-wide in-stock default, so parsed.filters is never empty and would
  // make every page look faceted.
  const isFacetedOrSorted =
    searchParams.has('sort') ||
    Array.from(searchParams.keys()).some((k) =>
      k.startsWith(FILTER_PARAM_PREFIX),
    );
  const canonical = canonicalUrl(
    request.url,
    !isFacetedOrSorted && parsed.page > 1 ? ['page'] : [],
  );

  // seoPayload.collection keeps the exact title/description/media behavior
  // this local object had, and adds the CollectionPage + ItemList +
  // BreadcrumbList JSON-LD the page never emitted. The collection from
  // COLLECTION_QUERY has no products — feed the ItemList from the native
  // page query's nodes, already in scope. titleTemplate is stripped to keep
  // the current '%s | <root>' title format.
  // Shopify holds no description for most collections (modular: description
  // "", seo.description null), so seoPayload.collection() truncated an empty
  // string and Hydrogen's <Seo> fell back to the root description — 559
  // collections, two locales, one meta description between them. Build one
  // from what the loader already has: the title, the count, and the top
  // vendors and product types from the facets. No two collections share it.
  const facetTop = (facetId: string, take: number) =>
    (facets.find((f) => f.id === facetId)?.values ?? [])
      .filter((v) => typeof v.count === 'number' && v.count > 0)
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
      .slice(0, take)
      .map((v) => v.label);
  const hasOwnDescription = Boolean(
    collection?.seo?.description?.trim() || collection?.description?.trim(),
  );
  const generatedDescription = hasOwnDescription
    ? undefined
    : collectionDescription({
        title: collection.title,
        count: totalProducts ?? products.length,
        vendors: facetTop(VENDOR_FILTER_ID, 4),
        types: facetTop(PRODUCT_TYPE_FACET_ID, 3),
        english: language === 'EN',
      });

  const {titleTemplate: _collectionTitleTemplate, ...collectionSeo} =
    seoPayload.collection({
      collection: {
        ...collection,
        products: {
          nodes: grouped
            ? grouped.nodes
            : outOfRange
            ? []
            : productsConnection?.nodes ?? [],
        },
      } as unknown as CollectionType,
      url: canonical,
      breadcrumb: (collectionBreadcrumb ?? [])
        .filter((item) => Boolean(item?.title && item?.handle))
        .map((item) => ({title: item.title, handle: item.handle})),
    });

  // seoPayload.collection only feeds `url` into its JSON-LD. The <Seo>
  // component reads the canonical from `seo.url`, and with none set on this
  // route the root loader's query-stripped value won every time — which is
  // why the page-2 canonical never changed. Set it explicitly.
  const seo = {
    ...collectionSeo,
    ...(generatedDescription ? {description: generatedDescription} : {}),
    url: canonical,
  };

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  // A brand's own collection gets the brand page's 型號 band: the vendor
  // page redirects here (see brandCollections.ts).
  const brandVendor = brandForCollection(collectionHandle);
  const brandModel = brandVendor
    ? selectedBrandModel(modelsForVendor(brandVendor), searchParams)
    : null;

  return json(
    {
      hasAds,
      collection,
      brandVendor: brandVendor ?? null,
      brandModel,
      analytics: {
        pageType: AnalyticsPageType.collection,
        collectionHandle,
        resourceId: collection.id,
      },
      translation: translation[selectedLocale],
      tagTranslation: tagTranslation[selectedLocale],
      seo,
      products,
      nativeFilters: {
        facets,
        priceBounds,
      },
      collectionAdvertisement,
      defaultAdvertisement,
      totalProducts,
      collectionBreadcrumb,
      relatedCollections,
      collectionsTranslated,
    },
    {
      headers: {
        'Cache-Control': PLP_CACHE_CONTROL,
      },
    },
  );
}

export default function Collection() {
  const {collection} = useLoaderData<typeof loader>();

  if (!collection) {
    return <></>;
  }

  return (
    <section className="lg:pt-10 relative">
      <Container>
        <CollectionMain />
      </Container>
    </section>
  );
}

const COLLECTION_QUERY = `#graphql
  query CollectionDetails(
    $handle: String!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      descriptionHtml
      seo {
        description
        title
      }
      image {
        id
        url
        width
        height
        altText
      }
    }
  }
`;

/**
 * A meta description for a collection Shopify left blank. Written from data
 * so it stays true as stock moves; kept under ~155 characters either way.
 */
function collectionDescription({
  title,
  count,
  vendors,
  types,
  english,
}: {
  title: string;
  count: number;
  vendors: string[];
  types: string[];
  english: boolean;
}): string {
  const list = (items: string[], sep: string) =>
    items.filter(Boolean).join(sep);
  if (english) {
    const parts = [
      `Shop ${
        count > 0 ? `${count} ` : ''
      }${title} at Helmetking, Hong Kong's largest online motorcycle gear store.`,
    ];
    if (vendors.length) parts.push(`Brands: ${list(vendors, ', ')}.`);
    if (types.length) parts.push(`${list(types, ', ')}.`);
    parts.push('Genuine stock, best price guarantee, Mong Kok store pickup.');
    return parts.join(' ').slice(0, 160);
  }
  const parts = [
    `頭盔王${title}${count > 0 ? `共 ${count} 款` : ''}，正貨現貨。`,
  ];
  if (vendors.length) parts.push(`品牌：${list(vendors, '、')}。`);
  if (types.length) parts.push(`${list(types, '、')}。`);
  parts.push('最低價保證，旺角門市可取貨。');
  return parts.join('').slice(0, 160);
}
